import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, 
  LogOut, 
  User, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Loader2,
  Wallet,
  Plus,
  Trash2,
  RefreshCw,
  Copy,
  MessageCircle,
  History,
  Settings
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function UserCabinetNew() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("history");
  const [requests, setRequests] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Profile edit state
  const [editTelegram, setEditTelegram] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  
  // New wallet state
  const [showAddWallet, setShowAddWallet] = useState(false);
  const [newWallet, setNewWallet] = useState({
    name: "",
    address: "",
    network: "ERC20",
    currency_symbol: "ETH"
  });
  const [savingWallet, setSavingWallet] = useState(false);

  const networks = [
    { value: "ERC20", label: "Ethereum (ERC20)" },
    { value: "TRC20", label: "Tron (TRC20)" },
    { value: "BEP20", label: "BSC (BEP20)" },
    { value: "Bitcoin", label: "Bitcoin" },
    { value: "Solana", label: "Solana" },
    { value: "Polygon", label: "Polygon" },
    { value: "Ripple", label: "Ripple (XRP)" },
  ];

  const getToken = () => localStorage.getItem("user_token");

  const checkAuth = useCallback(() => {
    const token = getToken();
    const userData = localStorage.getItem("user_data");
    if (!token || !userData) {
      navigate("/auth");
      return null;
    }
    return JSON.parse(userData);
  }, [navigate]);

  const fetchProfile = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/user/profile`, {
        params: { token }
      });
      setUser(response.data);
      setEditTelegram(response.data.telegram || "");
      localStorage.setItem("user_data", JSON.stringify(response.data));
    } catch (error) {
      console.error("Error fetching profile:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("user_token");
        localStorage.removeItem("user_data");
        navigate("/auth");
      }
    }
  }, [navigate]);

  const fetchHistory = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/user/exchange-history`, {
        params: { token }
      });
      setRequests(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  }, []);

  const fetchWallets = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/user/wallets`, {
        params: { token }
      });
      setWallets(response.data);
    } catch (error) {
      console.error("Error fetching wallets:", error);
    }
  }, []);

  useEffect(() => {
    const userData = checkAuth();
    if (userData) {
      setUser(userData);
      setEditTelegram(userData.telegram || "");
      
      Promise.all([fetchProfile(), fetchHistory(), fetchWallets()])
        .finally(() => setLoading(false));
    }
  }, [checkAuth, fetchProfile, fetchHistory, fetchWallets]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchHistory(), fetchWallets()]);
    setRefreshing(false);
    toast.success("Данные обновлены");
  };

  const handleLogout = () => {
    localStorage.removeItem("user_token");
    localStorage.removeItem("user_data");
    toast.success("Вы вышли из аккаунта");
    navigate("/");
  };

  const handleSaveProfile = async () => {
    const token = getToken();
    if (!token) return;
    
    setSavingProfile(true);
    try {
      await axios.put(`${API}/user/profile`, {
        telegram: editTelegram || null
      }, {
        params: { token }
      });
      await fetchProfile();
      toast.success("Профиль обновлён");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Ошибка обновления профиля");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAddWallet = async () => {
    if (!newWallet.name || !newWallet.address) {
      toast.error("Заполните название и адрес кошелька");
      return;
    }
    
    const token = getToken();
    if (!token) return;
    
    setSavingWallet(true);
    try {
      await axios.post(`${API}/user/wallets`, newWallet, {
        params: { token }
      });
      await fetchWallets();
      setNewWallet({ name: "", address: "", network: "ERC20", currency_symbol: "ETH" });
      setShowAddWallet(false);
      toast.success("Кошелёк сохранён");
    } catch (error) {
      console.error("Error adding wallet:", error);
      toast.error(error.response?.data?.detail || "Ошибка сохранения кошелька");
    } finally {
      setSavingWallet(false);
    }
  };

  const handleDeleteWallet = async (walletId) => {
    const token = getToken();
    if (!token) return;
    
    try {
      await axios.delete(`${API}/user/wallets/${walletId}`, {
        params: { token }
      });
      await fetchWallets();
      toast.success("Кошелёк удалён");
    } catch (error) {
      console.error("Error deleting wallet:", error);
      toast.error("Ошибка удаления кошелька");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Скопировано");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="text-green-500" size={20} />;
      case 'cancelled':
        return <XCircle className="text-red-500" size={20} />;
      case 'processing':
      case 'in_progress':
        return <Loader2 className="text-yellow-500 animate-spin" size={20} />;
      default:
        return <Clock className="text-yellow-500" size={20} />;
    }
  };

  const getStatusText = (status) => {
    const statuses = {
      pending: "Ожидание оплаты",
      awaiting_confirmation: "Ожидание подтверждения",
      deposit_confirmed: "Депозит подтверждён",
      in_progress: "В обработке",
      processing: "Обработка",
      completed: "Завершено",
      cancelled: "Отменено"
    };
    return statuses[status] || status;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-500';
      case 'cancelled': return 'text-red-500';
      case 'processing':
      case 'in_progress': return 'text-blue-500';
      default: return 'text-yellow-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/")}
              variant="ghost"
              size="sm"
              className="text-primary hover:text-primary/80"
            >
              <ArrowLeft size={20} className="mr-2" />
              На главную
            </Button>
            <h1 className="font-heading text-xl font-bold text-primary">
              ЛИЧНЫЙ КАБИНЕТ
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={handleRefresh}
              variant="ghost"
              size="sm"
              disabled={refreshing}
              className="text-muted-foreground hover:text-foreground"
            >
              <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
            </Button>
            <span className="text-sm text-muted-foreground hidden sm:block">
              {user?.email}
            </span>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
            >
              <LogOut size={18} className="mr-2" />
              Выйти
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button
            onClick={() => setActiveTab("history")}
            variant={activeTab === "history" ? "default" : "outline"}
            size="sm"
            className={activeTab === "history" ? "bg-primary" : ""}
          >
            <History size={16} className="mr-2" />
            История
          </Button>
          <Button
            onClick={() => setActiveTab("wallets")}
            variant={activeTab === "wallets" ? "default" : "outline"}
            size="sm"
            className={activeTab === "wallets" ? "bg-primary" : ""}
          >
            <Wallet size={16} className="mr-2" />
            Кошельки
          </Button>
          <Button
            onClick={() => setActiveTab("profile")}
            variant={activeTab === "profile" ? "default" : "outline"}
            size="sm"
            className={activeTab === "profile" ? "bg-primary" : ""}
          >
            <Settings size={16} className="mr-2" />
            Профиль
          </Button>
        </div>

        {/* History Tab */}
        {activeTab === "history" && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-foreground">
              {requests.length > 0 
                ? `Ваши заявки: ${requests.length}` 
                : "У вас пока нет заявок"}
            </h2>
            
            {requests.length === 0 && (
              <div className="text-center py-12 bg-card border border-border rounded-lg">
                <History size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">Начните обмен криптовалюты</p>
                <Button onClick={() => navigate("/")} className="bg-primary">
                  Обменять
                </Button>
              </div>
            )}
            
            {requests.map((request) => (
              <div
                key={request.id}
                className="bg-card border border-border rounded-lg p-4 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <span className="bg-primary text-primary-foreground font-bold text-sm px-2 py-1 rounded">
                        #{request.order_number || request.id.slice(0, 8)}
                      </span>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(request.status)}
                        <span className={`font-medium ${getStatusColor(request.status)}`}>
                          {getStatusText(request.status)}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(request.created_at).toLocaleString('ru-RU')}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Отправили</div>
                        <div className="font-mono font-bold text-foreground">
                          {request.from_amount} {request.from_currency?.toUpperCase()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground mb-1">Получите</div>
                        <div className="font-mono font-bold text-primary">
                          {parseFloat(request.to_amount).toFixed(8)} {request.to_currency?.toUpperCase()}
                        </div>
                      </div>
                    </div>

                    {request.user_wallet && (
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">Кошелёк:</span>
                        <span className="font-mono text-foreground truncate max-w-[200px]">
                          {request.user_wallet}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => copyToClipboard(request.user_wallet)}
                        >
                          <Copy size={12} />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Wallets Tab */}
        {activeTab === "wallets" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground">
                Сохранённые кошельки
              </h2>
              <Button
                onClick={() => setShowAddWallet(!showAddWallet)}
                variant="outline"
                size="sm"
                className="border-primary text-primary hover:bg-primary/10"
              >
                <Plus size={16} className="mr-2" />
                Добавить
              </Button>
            </div>

            {/* Add wallet form */}
            {showAddWallet && (
              <div className="bg-card border border-border rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-foreground">Новый кошелёк</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-sm">Название</Label>
                    <Input
                      value={newWallet.name}
                      onChange={(e) => setNewWallet({...newWallet, name: e.target.value})}
                      placeholder="Мой ETH кошелёк"
                      className="mt-1 bg-input border-border"
                    />
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-sm">Сеть</Label>
                    <select
                      value={newWallet.network}
                      onChange={(e) => setNewWallet({...newWallet, network: e.target.value})}
                      className="mt-1 w-full h-10 px-3 bg-input border border-border rounded-md text-foreground"
                    >
                      {networks.map(n => (
                        <option key={n.value} value={n.value}>{n.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Адрес кошелька</Label>
                  <Input
                    value={newWallet.address}
                    onChange={(e) => setNewWallet({...newWallet, address: e.target.value})}
                    placeholder="0x..."
                    className="mt-1 bg-input border-border font-mono"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleAddWallet}
                    disabled={savingWallet}
                    className="bg-primary"
                  >
                    {savingWallet ? <Loader2 className="animate-spin" size={16} /> : "Сохранить"}
                  </Button>
                  <Button
                    onClick={() => setShowAddWallet(false)}
                    variant="outline"
                  >
                    Отмена
                  </Button>
                </div>
              </div>
            )}

            {wallets.length === 0 && !showAddWallet && (
              <div className="text-center py-12 bg-card border border-border rounded-lg">
                <Wallet size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  Сохраните кошельки для быстрого обмена
                </p>
                <Button 
                  onClick={() => setShowAddWallet(true)} 
                  variant="outline"
                  className="border-primary text-primary"
                >
                  <Plus size={16} className="mr-2" />
                  Добавить кошелёк
                </Button>
              </div>
            )}

            {wallets.map((wallet) => (
              <div
                key={wallet.id}
                className="bg-card border border-border rounded-lg p-4 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-foreground">{wallet.name}</span>
                    <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                      {wallet.network}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm text-muted-foreground truncate max-w-[250px]">
                      {wallet.address}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => copyToClipboard(wallet.address)}
                    >
                      <Copy size={12} />
                    </Button>
                  </div>
                </div>
                <Button
                  onClick={() => handleDeleteWallet(wallet.id)}
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-bold text-foreground mb-6 flex items-center gap-2">
                <User size={20} />
                Личные данные
              </h2>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-muted-foreground text-sm">Email</Label>
                  <Input
                    value={user?.email || ""}
                    disabled
                    className="mt-1 bg-input/50 border-border text-muted-foreground"
                  />
                </div>
                
                <div>
                  <Label className="text-muted-foreground text-sm">
                    <MessageCircle size={14} className="inline mr-1" />
                    Telegram
                  </Label>
                  <Input
                    value={editTelegram}
                    onChange={(e) => setEditTelegram(e.target.value)}
                    placeholder="@username"
                    className="mt-1 bg-input border-border"
                  />
                </div>

                <div>
                  <Label className="text-muted-foreground text-sm">Дата регистрации</Label>
                  <Input
                    value={user?.created_at ? new Date(user.created_at).toLocaleDateString('ru-RU') : ""}
                    disabled
                    className="mt-1 bg-input/50 border-border text-muted-foreground"
                  />
                </div>

                <div className="pt-4">
                  <Button
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="bg-primary"
                  >
                    {savingProfile ? (
                      <Loader2 className="animate-spin mr-2" size={16} />
                    ) : null}
                    Сохранить изменения
                  </Button>
                </div>
              </div>
            </div>

            {/* KYC Section */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-bold text-foreground mb-4">
                Верификация (KYC)
              </h2>
              <div className="flex items-center gap-3">
                {user?.kyc_verified ? (
                  <>
                    <CheckCircle2 className="text-green-500" size={24} />
                    <span className="text-green-500 font-medium">Верифицирован</span>
                  </>
                ) : (
                  <>
                    <Clock className="text-yellow-500" size={24} />
                    <span className="text-muted-foreground">Не верифицирован (необязательно)</span>
                  </>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Верификация не обязательна для использования сервиса
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserCabinetNew;
